import { Formik } from 'formik'
import { cloneDeep, omit } from 'lodash'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import Loading from '@/components/Card/Loading'
import { Text } from '@/components/Form'
import { RemoveCircle } from '@/components/Icons'
import Modal from '@/components/Modal'
import { PrimaryButton, SecondaryButton } from '@/components/StyledComponents'
import { CreateFeatureProps, PatchFeatureProps } from '@/pages/SpaceAppBuilder/FeaturesPage/actions'
import * as actions from '@/pages/SpaceAppBuilder/FeaturesPage/actions'
import BoltIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/BoltIcon'
import { InputTypes } from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Notification/InputText'
import { getAppBuilderFeatureData } from '@/pages/SpaceAppBuilder/FeaturesPage/selectors'
import {
  ActionBlockDataType,
  ActionData,
  ActionTypes,
  Feature,
  FlowElement
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { useAppDispatch, useAppSelector } from '@/store/hooks'

import { actionFeatureSchema } from './actionFeatureSchema'
import messages from './messages'

const Title = styled.h2`
  font-weight: bold;
  font-size: 24px;
  color: inherit;
  margin: 5px 0 0 10px;
`

const Heading = styled.h3`
  font-weight: bold;
  font-size: 1rem;
  color: inherit;
  margin: 0;
`

const HeadingDescription = styled.p`
  color: var(--wg-gray);
  font-size: 0.8rem;
  margin-top: 10px;
`

const StyledText = styled(Text)`
  .field__label {
    font-weight: normal;
  }
`

const IconContainer = styled.div`
  width: 40px;
  height: 40px;

  align-items: center;
  justify-content: center;

  display: flex;
  background-color: #f6f6f6;
  border-radius: 3px;
  padding: 0.5rem;

  cursor: pointer;
`

const RightIcon = styled(IconContainer)`
  margin-left: auto;

  g {
    circle {
      fill: none;
    }
    path {
      stroke: black;
    }
  }
`

const Header = styled.div`
  display: flex;
  padding: 0.5rem;
`

const Content = styled.div`
  padding: 0 75px;
  margin-bottom: 3rem;

  .form__field {
    margin-top: 0.25rem;
  }
`

const ContentHeader = styled.div`
  padding-right: 75px;
  padding-left: 20px;
  display: flex;
`

const StyledBoltIcon = styled(BoltIcon)`
  margin-left: 30px;
  fill: var(--wg-dark-blue);
`

const StyledList = styled.ul`
  font-size: 0.875rem;
  li::marker {
    content: '✓ ';
  }
  li {
    margin-bottom: 1px;
  }
`

const ButtonRow = styled.div`
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
`

const Description = styled.p`
  color: var(--wg-gray);
  font-size: 1rem;
  margin-left: 10px;
  line-height: 1.2;
  margin-top: 10px;
`

const TriggerDataContainer = styled.div`
  margin-top: 2rem;
`

const DefaultTriggerDataContainer = styled.div`
  margin-left: 10px;
  margin-top: 20px;
  margin-bottom: 30px;
`

interface CreateFeatureModalProps {
  isOpen?: boolean
  notificationActions: ActionBlockDataType[]
  actionData: ActionData[]
  selectedElement: FlowElement
  onClose: () => void
  agentsFlagOn?: boolean
}

const CreateFeatureModal = ({
  isOpen = true,
  selectedElement,
  actionData,
  notificationActions,
  onClose,
  agentsFlagOn = false
}: CreateFeatureModalProps) => {
  const intl = useIntl()
  const { appId } = useParams<{ appId: string }>()

  const [isActionInProgress, setIsActionInProgress] = useState(false)
  const featureFetchState = useAppSelector(getAppBuilderFeatureData)
  const dispatch = useAppDispatch()
  const submitActionFeature = async (values: any) => {
    const actionLabels = notificationActions
      ?.filter((action: ActionBlockDataType) => action.type === ActionTypes.ActionSubmit)
      .map((action: ActionBlockDataType) => action.title)

    const trigger = {
      type: 'notificationAction',
      parentFeature: featureFetchState.feature.id,
      stepId: selectedElement.id,
      triggerData: {
        customEventData: actionData,
        actionSetButtonsLabels: actionLabels
      }
    }
    setIsActionInProgress(true)

    const createdActionFeature: any = await dispatch(
      actions.createAppInstanceFeature({
        instanceId: appId,
        feature: {
          name: values.name,
          trigger: 'notificationAction',
          workflow: {
            trigger,
            steps: []
          }
        }
      } as CreateFeatureProps)
    )

    const patchFeature = omit(cloneDeep(featureFetchState.feature), [
      'id',
      'workflow',
      'createdBy',
      'modifiedBy',
      'createdDate',
      'modifiedDate',
      'audience'
    ]) as Feature

    const relatedFeatures = patchFeature?.relatedFeatures || []

    await dispatch(
      actions.patchAppInstanceFeature({
        instanceId: appId,
        featureId: featureFetchState.feature.id,
        feature: {
          ...patchFeature,
          relatedFeatures: [
            ...relatedFeatures,
            {
              featureId: createdActionFeature?.id,
              stepId: selectedElement.id
            }
          ]
        }
      } as PatchFeatureProps)
    )

    setIsActionInProgress(false)
    onClose()
  }

  return (
    <Formik
      enableReinitialize
      validationSchema={actionFeatureSchema}
      onSubmit={submitActionFeature}
      initialValues={{
        name: ''
      }}
    >
      {({ handleSubmit }) => (
        <Modal isOpen={isOpen} onClose={onClose}>
          {isActionInProgress && (
            <div className="card__overlay">
              <Loading isLoading={true} loadingMessage={`Creating ${agentsFlagOn ? 'Capability' : 'Feature'}`} />
            </div>
          )}
          <Header>
            <RightIcon onClick={onClose}>
              <RemoveCircle />
            </RightIcon>
          </Header>
          <ContentHeader>
            <StyledBoltIcon />
            <div>
              <Title>Create {agentsFlagOn ? 'Capability' : 'Feature'}</Title>
              <Description>
                This new {agentsFlagOn ? 'capability' : 'feature'} will be linked to the button in the notification.
                When the button is actioned this {agentsFlagOn ? 'capability' : 'feature'} will be triggered.{' '}
              </Description>
            </div>
          </ContentHeader>
          <Content>
            <StyledText
              name="name"
              label={intl.formatMessage(messages.featureName)}
              placeholder={`Name your new action ${agentsFlagOn ? 'capability' : 'feature'}`}
            />

            <TriggerDataContainer>
              <Heading>{intl.formatMessage(messages.triggerData)}</Heading>
              <HeadingDescription>
                The following lists the payload data that will be sent to your new{' '}
                {agentsFlagOn ? 'capability' : 'feature'} when actioned
              </HeadingDescription>
              <DefaultTriggerDataContainer>
                <StyledList>
                  <li>
                    <FormattedMessage {...messages.actionLabel} />
                  </li>
                  <li>
                    <FormattedMessage {...messages.userID} />
                  </li>
                  <li>
                    <FormattedMessage {...messages.userEmail} />
                  </li>
                  {actionData
                    ?.filter(
                      (actionEventData: ActionData) =>
                        (actionEventData.attributeName && actionEventData.attributeValue) ||
                        (actionEventData.attributeName && actionEventData.inputType === InputTypes.InputText)
                    )
                    ?.map((actionEventData: ActionData) => (
                      <li key={actionEventData.attributeName}>{actionEventData.attributeName}</li>
                    ))}
                </StyledList>
              </DefaultTriggerDataContainer>
            </TriggerDataContainer>
          </Content>
          <ButtonRow>
            <SecondaryButton value={intl.formatMessage(messages.cancel)} type="button" onClick={onClose} />
            <PrimaryButton
              value={`Add ${agentsFlagOn ? 'capability' : 'feature'}`}
              type="button"
              onClick={() => handleSubmit()}
            />
          </ButtonRow>
        </Modal>
      )}
    </Formik>
  )
}

export default CreateFeatureModal
